import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { ProtectedRoute } from "@/hooks/useAuth";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { AppShell } from "@/components/layout/AppShell";
import { ComponentsGalleryPage } from "@/pages/ComponentsGalleryPage";
import { LoginPage } from "@/pages/LoginPage";
import { ProjectEditPage } from "@/pages/ProjectEditPage";
import { SharedPreviewPage } from "@/pages/SharedPreviewPage";
// PR-0 builder reorg: the home shows one card per project
// (``ProjectsHomePage``) and each project opens as a hub with its
// reports + data + loader + config (``ProjectHubPage``). Project
// CREATE keeps the full-page editor at /projects/new; the old
// /projects/:slug/edit URL redirects into the hub's config tab.
import { ProjectHubPage } from "@/pages/projects/ProjectHubPage";
import { ReportGroupPage } from "@/pages/projects/ReportGroupPage";
import { ProjectsHomePage } from "@/pages/projects/ProjectsHomePage";
import { ReportBuilderPage } from "@/pages/builder/ReportBuilderPage";
import { PromptEditPage } from "@/pages/admin/PromptEditPage";
// PR-4 builder reorg: Panorama IA at /admin/ai (resumen + issue queue
// + global prompts). The old /admin/prompts LIST redirects into its
// "prompts" tab — the /admin/prompts/:id EDITOR keeps its own route.
import { PanoramaIAPage } from "@/pages/panorama/PanoramaIAPage";
// PR-3 builder reorg had put portal user administration in the builder
// at /admin/users + /admin/users/:userId/permissions. Plan J J3 REPLACES
// both with Accesos (below): access is written on a GROUP now, never on
// a person, so a per-user permission matrix is a screen for a thing that
// no longer exists. `UsersAdminPage` and `UserPermissionsPage` are no
// longer imported — the two old paths redirect — and the files stay on
// disk until the legacy tables are parked (§F.3 of the plan), because
// deleting a page while its tables are still dual-written is how the
// rollback loses its landing site.
//
// Plan J J3 — Accesos: ámbitos, grupos, personas, auditoría. Absolute
// paths, one <Route> each, on purpose: `check_guide_keys.py::routes()`
// regex-reads `path="…"` and assertion F only inspects paths starting
// `/admin/`, so relative children of a layout route would be INVISIBLE
// to the gate. `AccessTabs.tsx` carries the same note from the other
// side.
import { AccessAuditPage } from "@/pages/admin-access/AccessAuditPage";
import { AccessGroupsPage } from "@/pages/admin-access/AccessGroupsPage";
import { AccessPeoplePage } from "@/pages/admin-access/AccessPeoplePage";
import { AccessScopesPage } from "@/pages/admin-access/AccessScopesPage";
import { GroupDetailPage } from "@/pages/admin-access/GroupDetailPage";
import { PersonDetailPage } from "@/pages/admin-access/PersonDetailPage";
import { ScopeDetailPage } from "@/pages/admin-access/ScopeDetailPage";
// PR-6 builder reorg: operations cockpit + builder guide.
import { OperationsPage } from "@/pages/operations/OperationsPage";
import { CachePage } from "@/pages/cache/CachePage";
// Plan C 2.1+2.3: the unified integrations manager (channels + data
// sources). Same gate as Operations — storing a credential VALUE is
// strictly more powerful than editing a project.
import { IntegrationsPage } from "@/pages/integrations/IntegrationsPage";
import { SendPolicyPage } from "@/pages/admin/SendPolicyPage";
import { GuidePage } from "@/pages/guide/GuidePage";

/**
 * Top-level route table.
 *
 * Public:
 *   /login
 *
 * Authenticated (wrapped in <ProtectedRoute><AppShell>…):
 *   /            → BuilderListPage
 *   /builder/:key → ReportBuilderPage (placeholder until Fase 3)
 *
 * Unknown paths redirect to `/`.
 */
export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Shareable read-only preview — sits outside the AppShell so stakeholders
          see the report without the editor chrome. Still requires auth. */}
      <Route
        path="/preview/:key"
        element={
          <ProtectedRoute>
            <SharedPreviewPage />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<ProjectsHomePage />} />
        {/* Plantillas removed (2026-05-18 audit revisit): the
            report-builder skill creates new reports via Claude rather
            than via templates. Old URLs redirect to the report list. */}
        <Route path="/templates" element={<Navigate to="/" replace />} />
        <Route
          path="/templates/:slug/edit"
          element={<Navigate to="/" replace />}
        />
        <Route path="/projects" element={<Navigate to="/" replace />} />
        <Route path="/projects/new" element={<ProjectEditPage />} />
        <Route path="/projects/:slug" element={<ProjectHubPage />} />
        {/* Report groups (UI3 A): the table filter × report. */}
        <Route path="/projects/:slug/groups/:groupKey" element={<ReportGroupPage />} />
        {/* Old bookmarks land on the hub's config tab. */}
        <Route path="/projects/:slug/edit" element={<ProjectEditRedirect />} />
        <Route path="/components" element={<ComponentsGalleryPage />} />
        <Route path="/builder/:key" element={<ReportBuilderPage />} />
        <Route
          path="/admin/ai"
          element={
            <AdminRoute flag="manage_ai">
              <PanoramaIAPage />
            </AdminRoute>
          }
        />
        {/* The prompts LIST lives inside Panorama IA now; old
            bookmarks land on its tab. */}
        <Route
          path="/admin/prompts"
          element={<Navigate to="/admin/ai?tab=prompts" replace />}
        />
        <Route
          path="/admin/prompts/:id"
          element={
            <AdminRoute flag="manage_ai">
              <PromptEditPage />
            </AdminRoute>
          }
        />
        {/* Plan J J3 — the two retired paths. INLINE <Navigate>, never a
            redirect COMPONENT: the gate reads the `<Route>` tag's own
            text and counts a chunk containing `<Navigate` as a redirect,
            which needs no help topic. A `<UserPermissionsRedirect />`
            would read as a LIVE route and demand help copy for a page
            that only redirects. Same shape as /admin/prompts above.

            The :userId is not carried across on purpose — there is no
            per-person permissions page to carry it TO. The person's own
            screen is /admin/access/people/:userId, and the migrated
            one-person group on it is the read-only view of what the old
            matrix showed; landing on the list is the honest answer to a
            bookmark of a screen that no longer exists. */}
        <Route
          path="/admin/users"
          element={<Navigate to="/admin/access/people" replace />}
        />
        <Route
          path="/admin/users/:userId/permissions"
          element={<Navigate to="/admin/access/people" replace />}
        />
        {/* Plan J J3 — Accesos. `manage_users` on every one of them, and
            that flag is deliberate: the plan cites `deps.py:175` for the
            guard and that line is inside `require_admin`'s docstring —
            the TENANT-ADMIN bypass. Wiring this surface to it would
            silently widen who may edit access. `RequireManageUsers`
            (`deps.py:253-255`) is the real guard the backend routers
            use, and `AdminRoute flag="manage_users"` is its front half. */}
        <Route
          path="/admin/access"
          element={<Navigate to="/admin/access/scopes" replace />}
        />
        <Route
          path="/admin/access/scopes"
          element={
            <AdminRoute flag="manage_users">
              <AccessScopesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/access/scopes/:scopeId"
          element={
            <AdminRoute flag="manage_users">
              <ScopeDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/access/groups"
          element={
            <AdminRoute flag="manage_users">
              <AccessGroupsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/access/groups/:groupId"
          element={
            <AdminRoute flag="manage_users">
              <GroupDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/access/people"
          element={
            <AdminRoute flag="manage_users">
              <AccessPeoplePage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/access/people/:userId"
          element={
            <AdminRoute flag="manage_users">
              <PersonDetailPage />
            </AdminRoute>
          }
        />
        {/* Auditoría. The read endpoint exists: `main.py` imports
            `access_audit_router` and mounts it, and the router declares
            prefix="/api/builder/admin/access-audit".

            (An earlier revision of this file carried a boxed comment
            saying the handler DID NOT EXIST, with the word "Verified"
            in it. It was false when it was written — `git log` puts the
            route in 04fb4d7c and that comment in 14b5427a, after it —
            and it sent readers off to build a handler that was already
            there. Recorded because a wrong comment that claims to have
            been checked costs more than no comment.)

            The route is listed here rather than left to the `*`
            catch-all: `AccessTabs` carries Auditoría as one of its four
            sections, and a tab that silently redirected to `/` would be
            a wrong answer with no error in it. */}
        <Route
          path="/admin/access/audit"
          element={
            <AdminRoute flag="manage_users">
              <AccessAuditPage />
            </AdminRoute>
          }
        />
        {/* PR-6 builder reorg: global operations cockpit + static
            builder guide. */}
        <Route
          path="/admin/operations"
          element={
            <AdminRoute flag="operations">
              <OperationsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/cache"
          element={
            <AdminRoute flag="operations">
              <CachePage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/integrations"
          element={
            <AdminRoute flag="operations">
              <IntegrationsPage />
            </AdminRoute>
          }
        />
        {/* Plan F, F4 — governs data leaving the platform, so the gate
            is the ROLE, not a capability flag (same stance as embed). */}
        <Route
          path="/admin/send-policy"
          element={
            <AdminRoute>
              <SendPolicyPage />
            </AdminRoute>
          }
        />
        <Route path="/guide" element={<GuidePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Bridges the retired /projects/:slug/edit URL into the hub. */
function ProjectEditRedirect(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  return (
    <Navigate to={`/projects/${encodeURIComponent(slug ?? "")}?tab=config`} replace />
  );
}
